import { MoreVert } from "@mui/icons-material";
import { TableCell } from "@mui/material";
import { StandardizedIconButton } from "common/components";
import { differenceInDays } from "date-fns";
import UpdateFeedEventDialog from "features/feedPurchaseDetail/UpdateFeedEventDialog";
import UpdateFeedPurchaseEventDialog from "features/feedPurchaseDetail/UpdateFeedPurchaseEventDialog";
import UpdateFertilizerEventDialog from "features/fertilizerInput/UpdateFertilizerEventDialog";
import UpdateFieldPreparationEventDialog from "features/fieldPreparationInput/UpdateFieldPreparationEventDialog";
import UpdateReseedEventDialog from "features/singleReseedEventInput/UpdateReseedEventDialog";
import UpdateYieldEventDialog from "features/yieldInput/UpdateYieldEventDialog";
import {
  FEED_EVENT_REJECTION_PERIOD_DAYS,
  FertilizerEvent,
  FieldPreparationEvent,
  FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS,
  FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS,
  GeneralFeedEvent,
  GeneralFeedPurchaseEvent,
  ReseedEvent,
  YieldEvent,
} from "model";
import { useState } from "react";

type CombinedEvent =
  | { type: "reseed"; event: ReseedEvent; date: Date }
  | { type: "feed"; event: GeneralFeedEvent; date: Date }
  | { type: "feedPurchase"; event: GeneralFeedPurchaseEvent; date: Date }
  | { type: "yield"; event: YieldEvent; date: Date }
  | { type: "fertilizer"; event: FertilizerEvent; date: Date }
  | { type: "fieldPreparation"; event: FieldPreparationEvent; date: Date };

interface EventActionMenuProps {
  combinedEvent: CombinedEvent;
}

export default function EventActionMenu({
  combinedEvent,
}: EventActionMenuProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }

  // Calculate canEdit based on event type and date
  const canEdit = (() => {
    const creationDate = combinedEvent.event.creationDate
      ? new Date(combinedEvent.event.creationDate)
      : Date.now();

    const daysSinceCreation = differenceInDays(Date.now(), creationDate);

    switch (combinedEvent.type) {
      case "reseed":
        return daysSinceCreation <= FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS;
      case "feed":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "feedPurchase":
        return (
          daysSinceCreation <= FIELD_PREPARATION_EVENT_REJECTION_PERIOD_DAYS
        );
      case "yield":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "fertilizer":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
      case "fieldPreparation":
        return daysSinceCreation <= FEED_EVENT_REJECTION_PERIOD_DAYS;
    }
  })();

  if (!canEdit) {
    return <TableCell sx={{ verticalAlign: "top" }}></TableCell>;
  }

  return (
    <TableCell sx={{ verticalAlign: "top" }}>
      <StandardizedIconButton
        edge="end"
        aria-haspopup="true"
        onClick={openMenu}
        id={combinedEvent.event.guid}
        size="small"
        iconColor="default"
      >
        <MoreVert />
      </StandardizedIconButton>
      {combinedEvent.type === "reseed" && (
        <UpdateReseedEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          reseedEvent={combinedEvent.event}
        />
      )}
      {combinedEvent.type === "feed" && (
        <UpdateFeedEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          feedEvent={combinedEvent.event}
        />
      )}
      {combinedEvent.type === "feedPurchase" && (
        <UpdateFeedPurchaseEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          feedPurchaseEvent={combinedEvent.event}
        />
      )}
      {combinedEvent.type === "yield" && (
        <UpdateYieldEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          yieldEvent={combinedEvent.event}
        />
      )}
      {combinedEvent.type === "fertilizer" && (
        <UpdateFertilizerEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          fertilizerEvent={combinedEvent.event}
        />
      )}
      {combinedEvent.type === "fieldPreparation" && (
        <UpdateFieldPreparationEventDialog
          closeMenu={closeMenu}
          menuAnchor={menuAnchor}
          fieldPreparationEvent={combinedEvent.event}
        />
      )}
    </TableCell>
  );
}
